// src/pages/orders/components/ShiftPickerSection.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Users, BookmarkCheck } from 'lucide-react';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import { getShifts, getShiftTypes } from '../../../services/api/messageCenterShifts';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './ShiftPickerSection.module.css';

const REMEMBER_ENABLED_KEY = 'shiftPickerRememberEnabled';
const REMEMBER_SHIFT_ID_KEY = 'shiftPickerRememberedShiftId';
const REMEMBER_DATE_KEY = 'shiftPickerRememberedDate';

const todayYMD = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const findActiveShift = (shifts) => {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const toMinutes = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };

  return shifts.find(shift => {
    const start = toMinutes(shift.shiftType?.defaultStartTime);
    const end = toMinutes(shift.shiftType?.defaultEndTime);
    if (start == null || end == null) return false;
    return end > start ? (nowMinutes >= start && nowMinutes < end) : (nowMinutes >= start || nowMinutes < end);
  });
};

const ShiftPickerSection = ({ value, onChange, disabled = false, initialDate = null, autoSelectActiveShift = true, rememberable = false }) => {
  const { t } = useLocalization();
  const [rememberEnabled, setRememberEnabled] = useState(
    () => rememberable && localStorage.getItem(REMEMBER_ENABLED_KEY) === 'true'
  );
  const [date, setDate] = useState(() => {
    if (initialDate) return initialDate;
    if (rememberable && localStorage.getItem(REMEMBER_ENABLED_KEY) === 'true') {
      const rememberedDate = localStorage.getItem(REMEMBER_DATE_KEY);
      if (rememberedDate) return rememberedDate;
    }
    return autoSelectActiveShift ? todayYMD() : '';
  });
  const [shiftsForDate, setShiftsForDate] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const autoAppliedDateRef = useRef(null);
  const userPickedDateRef = useRef(false);
  const rememberAppliedRef = useRef(false);

  useEffect(() => {
    if (initialDate) setDate(initialDate);
  }, [initialDate]);

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => { valueRef.current = value; }, [value]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    let cancelled = false;

    const loadShifts = async () => {
      if (!date) {
        setShiftsForDate([]);
        if (autoSelectActiveShift || userPickedDateRef.current) onChangeRef.current(null);
        return;
      }
      try {
        setIsLoading(true);
        const response = await getShifts({ dateFrom: date, dateTo: date, pageSize: 50, activeTypesOnly: true });
        if (cancelled) return;
        const shifts = response.shifts || [];

        // Keep an already-linked shift selectable even if its shift type was deactivated since.
        if (valueRef.current != null && !shifts.some(s => s.id === valueRef.current)) {
          const all = await getShifts({ dateFrom: date, dateTo: date, pageSize: 50 });
          if (cancelled) return;
          const linked = (all.shifts || []).find(s => s.id === valueRef.current);
          if (linked) shifts.push(linked);
        }
        setShiftsForDate(shifts);

        const currentValueValid = valueRef.current != null && shifts.some(s => s.id === valueRef.current);

        if (rememberEnabled && !rememberAppliedRef.current && !currentValueValid) {
          rememberAppliedRef.current = true;
          const rememberedShiftId = parseInt(localStorage.getItem(REMEMBER_SHIFT_ID_KEY), 10);
          const rememberedShift = shifts.find(s => s.id === rememberedShiftId);
          if (rememberedShift) {
            onChangeRef.current(rememberedShift.id);
            return;
          }
        }

        if ((autoSelectActiveShift || userPickedDateRef.current) && !currentValueValid && autoAppliedDateRef.current !== date) {
          autoAppliedDateRef.current = date;
          const activeShift = findActiveShift(shifts);
          onChangeRef.current(activeShift ? activeShift.id : null);
        }
      } catch (err) {
        console.error('Failed to load shifts for date:', err);
        if (!cancelled) setShiftsForDate([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadShifts();

    return () => { cancelled = true; };
  }, [date, autoSelectActiveShift, rememberEnabled]);

  // Persist the current selection while "remember shift" is on, so the next
  // Create Order visit can restore it instead of defaulting to the active shift.
  useEffect(() => {
    if (!rememberable || !rememberEnabled) return;
    localStorage.setItem(REMEMBER_DATE_KEY, date || '');
  }, [rememberable, rememberEnabled, date]);

  useEffect(() => {
    if (!rememberable || !rememberEnabled || value == null) return;
    localStorage.setItem(REMEMBER_SHIFT_ID_KEY, String(value));
  }, [rememberable, rememberEnabled, value]);

  const handleToggleRemember = (checked) => {
    setRememberEnabled(checked);
    localStorage.setItem(REMEMBER_ENABLED_KEY, checked ? 'true' : 'false');
    if (checked) {
      if (date) localStorage.setItem(REMEMBER_DATE_KEY, date);
      if (value != null) localStorage.setItem(REMEMBER_SHIFT_ID_KEY, String(value));
    }
  };

  const shiftOptions = useMemo(() => shiftsForDate.map(shift => ({
    id: shift.id,
    label: `${shift.shiftType?.name || 'Shift'}${shift.shiftLeader?.name ? ` — ${shift.shiftLeader.name}` : ''}`,
  })), [shiftsForDate]);

  return (
    <div className={styles.wrapper}>
      <Users size={13} className={styles.icon} />
      <div className={styles.datePickerWrap}>
        <DatePicker
          value={date}
          onChange={(d) => { userPickedDateRef.current = true; setDate(d); }}
          placeholder={t('ecomOrders.selectDate') || 'Select date'}
          maxDate={todayYMD()}
          size="compact"
          dropdownPosition="top"
          disabled={disabled}
        />
      </div>
      <div className={styles.shiftDropdownWrap} title={t('ecomOrders.selectShift') || 'Select shift'}>
        <Dropdown
          value={value ?? ''}
          onChange={(v) => onChange(v != null && v !== '' ? parseInt(v, 10) : null)}
          options={shiftOptions}
          displayKey="label"
          valueKey="id"
          placeholder={isLoading ? (t('common.loading') || 'Loading...') : (shiftOptions.length === 0 ? (t('ecomOrders.noShiftsForDate') || 'No shifts for date') : (t('ecomOrders.selectShift') || 'Select shift'))}
          size="compact"
          disabled={disabled || isLoading || shiftOptions.length === 0}
        />
      </div>
      {rememberable && (
        <label
          className={styles.rememberToggle}
          title={t('ecomOrders.rememberShiftHint') || 'Remember this shift so it loads automatically next time'}
        >
          <input
            type="checkbox"
            checked={rememberEnabled}
            onChange={(e) => handleToggleRemember(e.target.checked)}
            disabled={disabled}
          />
          <BookmarkCheck size={12} />
          <span>{t('ecomOrders.rememberShift') || 'Remember'}</span>
        </label>
      )}
    </div>
  );
};

export default ShiftPickerSection;
